/**
 * WHETHER THIS BROWSER HAS A SCOUT SESSION — ASKED ONCE, AND ANSWERED BY SCOUT.
 *
 * THE WORKSPACE HOLDS NO CREDENTIAL, SO IT CANNOT KNOW ONE WAY OR THE OTHER BY
 * LOOKING AT ITSELF. There is no token in storage to test, no cookie this code
 * may read, and no client-side flag that would mean anything: the session
 * belongs to the identity provider and the Worker is the only party that can
 * verify it. So the question "am I signed in" is asked as a REQUEST, and the
 * answer is whatever Scout said — including when Scout says it is not there.
 *
 * WHY THIS EXISTS AT ALL, when every view already renders its own 401 panel.
 * Because a signed-out visitor was shown the whole workspace chrome — a sidebar
 * of thirteen destinations, a bottom bar, a "Views" button — and then thirteen
 * separate refusals inside it. That is not a sign-in experience and it is not a
 * landing page; it is the product's interior with the lights off, and the
 * Owner's brief asks for a real pre-login surface instead. The gate is what
 * makes that possible, and it is deliberately the THINNEST thing that can: one
 * read, one signal, and no second source of truth about who anyone is.
 *
 * THE PROBE IS A READ THE WORKSPACE ALREADY MAKES. `GET /gateway/capabilities`
 * is owner-gated, read-only, runs no model, and is written by nothing — so
 * asking it "is this a session" cannot create a conversation, cannot spend
 * money, and cannot change a record. That matters more than the few
 * milliseconds a purpose-built route would have saved: a probe with a side
 * effect would make simply LOADING the page a mutation.
 *
 * FIVE OUTCOMES, AND THE DIFFERENCES ARE THE POINT. `signedOut` and
 * `notOwner` are both refusals and are fixed by different things; `unavailable`
 * is not a refusal at all — it is a deployment that cannot authenticate anyone,
 * which no amount of signing in will change. Collapsing them would tell the
 * Owner to retry when retrying cannot work, which is the failure mode
 * `views/failure.tsx` was extracted to prevent one level down.
 */

import { signal } from '@preact/signals';
import {
	isAuthenticationFailure,
	isNotTheOwner,
	onAuthenticationFailure,
	readGateway,
	type GatewayError,
	type GatewayResult,
} from './gateway';

/** HOW A REFUSAL WAS REACHED, WHICH CHANGES WHAT THE LANDING SHOULD SAY. */
export type RefusalOrigin =
	/** The boot probe asked, and was refused. Nothing was open; this is simply
	 *  a browser without a session. */
	| 'boot'
	/** A read was refused while the Owner was USING the workspace. Something
	 *  was open and it has just stopped working, and that is worth a sentence —
	 *  a page that silently becomes a sign-in screen with no explanation is the
	 *  kind of thing that makes a person think they were logged out at random. */
	| 'during-session';

/** WHAT THIS BROWSER KNOWS ABOUT ITS SESSION, AND NOTHING MORE. */
export type SessionState =
	/** The question has been asked and Scout has not answered yet. */
	| { kind: 'probing' }
	/** Scout answered an owner-gated read. There is a session and it is the Owner's. */
	| { kind: 'signedIn'; checkedAt: string }
	/** No session: Scout said 401, or the edge answered the request itself with a
	 *  redirect to the identity provider. Both are fixed by the same door. */
	| { kind: 'signedOut'; error: GatewayError; via: RefusalOrigin }
	/** A session was verified, and it belongs to somebody who is not the Owner. */
	| { kind: 'notOwner'; error: GatewayError; via: RefusalOrigin }
	/** Scout ANSWERED, and SAID it is deliberately not serving — a deployment
	 *  state, not a network fault and not a refusal about identity. Fixed by an
	 *  operator, never by the visitor and never by waiting. */
	| { kind: 'maintenance'; error: GatewayError; via: RefusalOrigin }
	/** Scout ANSWERED and REFUSED, for a reason that is not about identity and
	 *  not about deployment state — a hostname this deployment does not answer
	 *  to, a request refused as cross-site. Signing in again changes nothing, and
	 *  neither does waiting. */
	| { kind: 'refused'; error: GatewayError; via: RefusalOrigin }
	/** Scout could not be asked at all, or answered in a way that is not one of
	 *  its own refusals. NOT fixed by signing in. */
	| { kind: 'unavailable'; error: GatewayError; via: RefusalOrigin };

export const session = signal<SessionState>({ kind: 'probing' });

/**
 * THE PROBE'S ROUTE, NAMED SO A SPECIMEN CAN ASSERT WHAT IS ASKED.
 *
 * It is exported rather than inlined because the choice is a claim — "this read
 * is safe to make on every cold load" — and a claim that can be checked is
 * worth more than one that reads well in a comment.
 */
export const SESSION_PROBE_PATH = '/api/scout/gateway/capabilities';

/**
 * HOW A REFUSAL BECOMES A STATE.
 *
 * THE STATUS IS THE SIGNAL AND THE NAMES ARE THE DETAIL, which is the same
 * rule `gateway.ts` already applies to its classifiers. A `401` is a session
 * problem whatever name it carries; a `403` is an identity problem; everything
 * else is Scout not being able to answer usefully.
 *
 * AND ONE REFINEMENT OF "EVERYTHING ELSE", BECAUSE IT WAS SAYING SOMETHING
 * UNTRUE. Until now every non-401, non-403 outcome collapsed into `unavailable`,
 * whose copy is *"Scout could not be reached"* — a claim about the NETWORK.
 * That is false for a whole family of answers in which Scout replies perfectly
 * well, in its own envelope, to say that it is deliberately not serving: a
 * database behind the code (`ScoutSchemaNotReady`), bookkeeping that disagrees
 * with the binding (`ScoutSchemaUnexpected`), no database bound at all
 * (`ScoutSchemaUnavailable`), no owner credential provisioned
 * (`ScoutOwnerAuthUnavailable`), a half-configured identity provider
 * (`ScoutAccessMisconfigured`), and — the state a cutover actually spends its
 * window in — a deployment HELD ON PURPOSE until its release is authorized
 * (`ScoutReleaseHeld`).
 *
 * THE COST OF THE OLD COLLAPSE WAS NOT JUST WRONG WORDS. `unavailable` carries
 * the page's ONLY retry control, on the reasoning that the network coming back
 * can fix it. None of the states above is fixed by retrying, and one of them —
 * a deployment in a controlled maintenance window — is a deployment that is
 * refusing ON PURPOSE, where a retry button is an invitation to hammer it.
 * Worse, the page told the reader to wait for the network, so the one person who
 * could actually fix it was told the wrong thing.
 *
 * SO THE RULE IS NOW: did Scout ANSWER? If its own envelope came back naming a
 * deployment state, that is `maintenance` — actionable, not transient, and never
 * retried. If nothing usable came back, it is still `unavailable`, and the retry
 * stays where it was always true.
 *
 * THE NAMES ARE MATCHED, NOT THE STATUS. Every one of them ships as a 503 today,
 * but the status is not what makes them true — a deployment answering a
 * maintenance state under a different status would be the same fact, and a 503
 * from some future middlebox would not be.
 *
 * ============================================================================
 * AND A 403 IS NOT ALWAYS "WRONG OWNER" — THE CORRECTION PHASE 6A MEASURED
 * ============================================================================
 *
 * This classifier used to read `status === 403` as "the identity was verified
 * and it is not the one this deployment answers to". That is one of the things a
 * 403 means here and it is not the only one: the Worker also refuses with 403
 * when the ARRIVING HOSTNAME is not the one it was configured to answer to, and
 * when a request is refused as cross-site. (The configuration variable that
 * holds that list is not named here: a `SCOUT_`-prefixed name in this tree is
 * indistinguishable from a Worker binding that leaked into the browser, and the
 * build refuses one. See `src/web/chat.ts`.) A visitor refused because they
 * reached the
 * deployment by another name would have been told something checkably untrue
 * about their own identity — and, because that branch renders no sign-in control,
 * they would have been told it with the door closed. Phase 6A named this the
 * second of three defects and it is `ScoutHostNotAllowed` on a hostname with
 * three claimants, which is exactly the state this deployment is in.
 *
 * SO IDENTITY IS MATCHED BY NAME (the `Scout*NotOwner` family, which is already
 * written down and already tested) and every other 403 is `refused` — a real
 * answer from Scout saying no, with its own reason travelling through untouched.
 */
const DEPLOYMENT_STATE_NAMES = new Set([
	'ScoutSchemaNotReady',
	'ScoutSchemaUnexpected',
	'ScoutSchemaUnavailable',
	'ScoutSchemaUnreadable',
	'ScoutOwnerAuthUnavailable',
	'ScoutAccessMisconfigured',
	// THE DELIBERATE HOLD. It belongs here for the same reason as the schema
	// states — Scout answered, in its own envelope, to say it is not serving —
	// and more sharply than any of them: a release hold is a deployment refusing
	// because a person decided it should, so a retry control would be an
	// invitation to hammer a deployment that is behaving exactly as intended.
	'ScoutReleaseHeld',
]);

/**
 * WHETHER SCOUT ANSWERED, IN ITS OWN ENVELOPE, TO SAY IT IS DELIBERATELY NOT
 * SERVING. Exported because the shell and a view inside it must reach the SAME
 * verdict about one error — the boot gate knew about the release hold and the
 * view-level panel did not, so a hold armed while the workspace was open invited
 * the Owner to retry a deployment that was refusing on purpose (Phase 6A §16.3).
 * One list, two readers.
 */
export function isDeploymentState(error: GatewayError): boolean {
	return DEPLOYMENT_STATE_NAMES.has(error.name);
}

/**
 * WHETHER SCOUT ANSWERED AND DECLINED, FOR A REASON THAT IS NOT IDENTITY.
 *
 * 403 means "authenticated, and not permitted" — and the identity half of that
 * is a NAMED, closed family. Everything else the Worker refuses with a 403 is
 * about the request rather than the person: the hostname it arrived by
 * (`ScoutHostNotAllowed`), or a request judged cross-site
 * (`ScoutCrossSiteRefused`). Telling a visitor their identity was checked and
 * found wanting, when what happened is that they used another hostname, is a
 * false statement about them, and it closes the sign-in door while making it.
 *
 * THE CONFIGURATION VARIABLE THAT CAUSES THE FIRST OF THOSE IS NOT NAMED HERE,
 * and that is the workspace's own rule rather than a stylistic choice: a
 * `SCOUT_`-prefixed name in this tree is indistinguishable from a Worker binding
 * that leaked into the browser, and the build refuses one. See `src/web/chat.ts`.
 */
export function isRefusedRequest(error: GatewayError): boolean {
	return error.status === 403 && !isNotTheOwner(error);
}

function stateForFailure(error: GatewayError, via: RefusalOrigin): SessionState {
	// THE ORDER IS THE ARGUMENT. Identity first, because "no session" and "not
	// this identity" are the two the visitor can act on; then the deployment
	// states, which are the two an operator acts on and which no visitor action
	// changes; then a refusal that is neither, which must not be dressed as
	// either.
	if (isAuthenticationFailure(error)) return { kind: 'signedOut', error, via };
	if (isNotTheOwner(error)) return { kind: 'notOwner', error, via };
	if (isDeploymentState(error)) return { kind: 'maintenance', error, via };
	if (isRefusedRequest(error)) return { kind: 'refused', error, via };
	return { kind: 'unavailable', error, via };
}

/**
 * WHETHER A FAILURE PUT THIS SESSION BACK TO THE LANDING.
 *
 * `unavailable` is deliberately NOT one of them. A deployment that cannot
 * authenticate anyone is a state the landing itself has to explain, so it is
 * the landing that is shown — but a WORKSPACE that is already open must not be
 * torn down because a network blip looked like an outage. Only a refusal that
 * names the session ends the session.
 */
export function endsTheSession(state: SessionState): boolean {
	return state.kind === 'signedOut' || state.kind === 'notOwner';
}

/** Ask Scout whether this browser has a session. Safe to call again. */
export async function probeSession(): Promise<SessionState> {
	session.value = { kind: 'probing' };
	const result: GatewayResult<unknown> = await readGateway(SESSION_PROBE_PATH, {
		// A cold load should not hang on a deployment that is not answering. The
		// read itself is fast — no model, no research — so a short bound is
		// honest rather than optimistic.
		timeoutMs: 15_000,
	});
	const next: SessionState = result.ok
		? { kind: 'signedIn', checkedAt: new Date().toISOString() }
		: stateForFailure(result.error, 'boot');
	session.value = next;
	return next;
}

/**
 * A READ THAT WAS REFUSED BECAUSE THE SESSION ENDED, REPORTED HERE.
 *
 * WHY AN OBSERVER RATHER THAN RESPONSES FROM THIRTEEN VIEWS. Every view already
 * renders its own refusal correctly, and that is exactly the problem: a session
 * that expires while the Owner is reading the Observatory leaves them on the
 * Observatory looking at a 401 panel, with the rest of the chrome still
 * offering destinations that will each refuse in turn. The honest thing is for
 * the shell to notice once and return to the landing with the reason, and the
 * only place that sees EVERY read is the transport.
 *
 * IT IS AN OBSERVER AND NOT A COUPLING. The registry lives in `gateway.ts`, and
 * this is the module that subscribes to it — so the dependency points one way
 * and `gateway.ts` stays unaware that a session concept exists at all.
 */
export function observeAuthenticationFailures(): () => void {
	return onAuthenticationFailure((error) => {
		// A PROBE'S OWN FAILURE ALREADY SET THE STATE. Re-entering here would
		// only recompute the same value, and it would do it while `probeSession`
		// is mid-assignment.
		if (session.value.kind === 'probing') return;
		session.value = stateForFailure(error, 'during-session');
	});
}

/**
 * ENDING THE SESSION, AND SAYING EXACTLY HOW FAR THAT GOES.
 *
 * There are two sessions in play and this workspace owns neither of them:
 *
 *   1. THE IDENTITY PROVIDER'S. Cloudflare Access holds it, and Cloudflare
 *      Access ends it at `/cdn-cgi/access/logout` on the protected hostname.
 *      That is the only place it can be ended.
 *   2. THIS BROWSER'S VIEW OF IT — the signal above, and anything the service
 *      worker has cached. That part IS ours and is cleared here.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It does not claim the sign-out succeeded.
 * `returnTo` is a navigation, and whether the identity provider acts on it is
 * not something this code can observe — the page is leaving. So the control
 * that calls this says what it asks for rather than what it got, and the Slice
 * 10 checkpoint records that the integrated identity-provider flow is
 * unverified. (The deployment's login method was an OAuth provider when that
 * was written and is Cloudflare's one-time PIN now; this code is the same,
 * because it never knew which one it was talking to.)
 *
 * NOTHING IS STORED, SO NOTHING HAS TO BE ERASED. There is no token in
 * `localStorage`, no credential in `sessionStorage` and no Scout cookie — the
 * only key this workspace ever writes is the appearance preference" — which was
 * FALSE, and is corrected here rather than left as the one uncheckable sentence
 * on a page whose whole point is being checkable. `index.html` READS
 * `scout:appearance` at boot, so a browser that has been given an appearance
 * paints it before the bundle loads; nothing in the shipped product has ever
 * set it. This workspace writes NOTHING to browser storage. The purge below
 * exists for the service worker's shell cache — the one thing on this device
 * that could outlive the session.
 */
export const ACCESS_LOGOUT_PATH = '/cdn-cgi/access/logout';

/**
 * WHERE "SIGN IN" GOES — THE ONE ADDRESS THAT MAKES THE IDENTITY PROVIDER ACT.
 *
 * IT IS NOT A WORKSPACE ROUTE, AND THAT IS THE WHOLE OF PHASE 6A'S FINDING. The
 * landing used to link to `/chat`, on the reasoning that a deployment's identity
 * provider would intercept a request for the workspace. Under the deployment's
 * actual, minimal Access configuration it does not: Access attaches to a PATH,
 * that path is `/api/scout/*`, and `/chat` is a static file the edge hands over
 * without asking anybody's name. The browser then arrives at a shell that asks
 * its protected question, is refused, and renders the landing it just left — so
 * the Control was a loop that looked like a button.
 *
 * THE DOOR IS THE WORKER'S OWN, INSIDE THE PROTECTED PREFIX. `GET
 * /api/scout/gateway/entry` is owner-gated like every other gateway route and
 * answers a 302 to the front door — but a browser NAVIGATING to it is
 * intercepted at the edge first, so the identity provider authenticates the
 * person and only then does the redirect happen. The Owner presses Sign in once
 * and arrives in the workspace; the client never fetches a login page, parses
 * one as JSON, or pretends a redirect is a malformed answer.
 *
 * IT IS A PATH AND NOT A CONSTRUCTED URL. No `redirect_url` query, no team
 * domain, no application id: this workspace knows nothing about which identity
 * provider is in front of it, and a page that built an authentication URL would
 * be a second implementation of the deployment's authentication.
 */
export const SIGN_IN_PATH = '/api/scout/gateway/entry';

export interface SignOutOutcome {
	/** Whether the service worker confirmed it cleared its caches. `null` when
	 *  there is no service worker to ask — which is the honest answer on a
	 *  browser that never registered one, not `false`. */
	cachesCleared: boolean | null;
}

/** Clear everything this workspace holds, and ask the identity provider to end
 *  the real session. The caller navigates; this does not, so it can be proved. */
export async function signOutLocally(): Promise<SignOutOutcome> {
	session.value = {
		kind: 'signedOut',
		via: 'during-session',
		error: {
			name: 'ScoutSignedOut',
			message: 'This browser signed out of Scout. The identity provider session was asked to end.',
			status: 401,
		},
	};
	// THE PAGE'S CONTROLLER, NOT ITS REGISTRATION. `postMessage` reaches the
	// worker that is actually serving THIS page; a registration that exists but
	// has no active worker yet would accept the message and do nothing with it,
	// and the sign-out would report a purge that never ran.
	const controller =
		typeof navigator === 'undefined' ? null : navigator.serviceWorker?.controller ?? null;
	if (controller === null) return { cachesCleared: null };
	return await new Promise<SignOutOutcome>((resolve) => {
		// A SERVICE WORKER THAT NEVER ANSWERS MUST NOT HOLD THE SIGN-OUT. The
		// message is a courtesy to the cache; the session signal is already
		// cleared above, and a page that refused to leave because a background
		// script was wedged would be the tail wagging the dog.
		const timer = setTimeout(() => resolve({ cachesCleared: null }), 2_000);
		const channel = new MessageChannel();
		channel.port1.onmessage = (event: MessageEvent) => {
			clearTimeout(timer);
			const data = event.data as { cleared?: unknown } | null;
			resolve({ cachesCleared: data?.cleared === true });
		};
		controller.postMessage({ type: 'scout:sign-out' }, [channel.port2]);
	});
}
